import { Observable } from 'rxjs';
import {
	AccessDeniedError,
	IMobizHttpService,
	IMobizStateService,
} from '../../common-react/core';
import { Inject, Injectable, InjectionToken } from '../../common-react/injection';

import { HsClassificationCalculateResponse } from '../data/hs-classification-calculate-response.do';
import { PosturinnApiEndpointConfiguration } from '../other/api-endpoint-configuration.interface';
import { PosturinnServerApi } from '../other/posturinn-server-api.enum';

/**
 * Parameters for a HS code lookup against the Mobiz duties API. `name` is the free-text
 * search term (a product description or a raw HS code), `categories` a single category name
 * (the property is plural for API compatibility) and `configuration` carries the recipient
 * country and, when the user typed a numeric HS code, the provided code itself.
 */
export interface IHsCodeSearchParams {
	name: string;
	categories?: string;
	description?: string;
	countryOfOrigin?: string;
	configuration?: {
		shipToCountries?: string;
		hsCodeProvided?: string;
	};
}

export interface ICustomsService {
	searchHsCode(params: IHsCodeSearchParams): Observable<HsClassificationCalculateResponse>;
	searchHsCodeForAnonymous(params: IHsCodeSearchParams): Observable<HsClassificationCalculateResponse>;
}

export const ICustomsService = new InjectionToken<ICustomsService>('ICustomsService');

@Injectable()
export class CustomsService implements ICustomsService {
	private get accountId(): string {
		if (this.stateService.currentMembership.value === null) {
			throw new AccessDeniedError('Missing membership data.');
		}
		return this.stateService.currentMembership.value.organizationId;
	}

	constructor(
		@Inject(IMobizHttpService)
		private httpService: IMobizHttpService,
		@Inject(IMobizStateService)
		private stateService: IMobizStateService
	) {}

	public searchHsCode(params: IHsCodeSearchParams): Observable<HsClassificationCalculateResponse> {
		const config: PosturinnApiEndpointConfiguration = {
			serverApi: PosturinnServerApi.CalculateDuties,
			urlParameters: {
				accountId: this.accountId,
			},
			body: JSON.stringify(params),
		};
		return this.httpService.post(new HsClassificationCalculateResponse(), config);
	}

	/**
	 * HS code lookup for the unauthenticated (anonymous) register-shipment flow. Uses the
	 * account-less duties endpoint so no membership/accountId is required.
	 */
	public searchHsCodeForAnonymous(params: IHsCodeSearchParams): Observable<HsClassificationCalculateResponse> {
		const config: PosturinnApiEndpointConfiguration = {
			serverApi: PosturinnServerApi.CalculateDutiesForAnonymous,
			body: JSON.stringify(params),
		};
		return this.httpService.post(new HsClassificationCalculateResponse(), config);
	}
}
